import { matchPath } from 'app/router/Routes'
import { isFalsy, isTruthy } from 'utilsTS'
import { parseFormLabelText } from './common/Common'
import { evaluateCustomFormula, isCustomFormulaValid } from './components/conditions/CustomConditionHelpers'
import {
  collectionConditions,
  formConditions,
  routeOptions,
  sfFieldToConditionType,
  specialFormConditions
} from './editor/ConditionalElementEditor'
import { collectionElementTypes } from './editor/FormWizard'
import { ConnectedMap, FormCondition, FormElement, FormType } from './FormTypes'

interface IsDisabledProps {
  item: FormElement
  formViewType?: FormType
  [key: string]: any
}

const isDisabled = ({ item, formViewType, ...props }: IsDisabledProps): boolean => {
  if ((item.conditions == null) || item.conditions.length === 0) {
    return false
  }

  const isTargetedVisibilityCondition = (condition, state) => condition.conditionTarget && ['hide', 'show'].includes(state) && !condition.sfField

  if (formViewType === 'fillable-pdf') {
    const hasTargetedCondition = item.conditions.some(
      (condition) => {
        if (condition.conditions != null) {
          return condition.conditions.some(sub => isTargetedVisibilityCondition(sub, condition.state))
        } else {
          return isTargetedVisibilityCondition(condition, condition.state)
        }
      }
    )

    const hasNonTargetedCondition = item.conditions.some(
      (condition) => {
        if (condition.conditions != null) {
          return condition.conditions.some(sub => !isTargetedVisibilityCondition(sub, condition.state))
        } else {
          return !isTargetedVisibilityCondition(condition, condition.state)
        }
      }
    )

    // If we have item that appear only based on other elements in the form we want to display it in this view
    if (hasTargetedCondition && !hasNonTargetedCondition) {
      return false
    }
  }

  return item.conditions.some((condition) => {
    const { conditionMet, state } = isConditionMet({
      condition,
      ...props
    })
    return (
      (conditionMet && state === 'hide') || (!conditionMet && state === 'show')
    )
  })
}

interface CheckAltLabelProps {
  item: FormElement
  elementsMap: Record<string, FormElement>
  values: Record<string, any>
  connectedMap: ConnectedMap
  errors: Record<string, any>
  langVersion?: string
  conditionType?: string
}

export const checkAltLabel = ({
  item,
  elementsMap,
  values,
  connectedMap,
  errors,
  langVersion,
  conditionType = 'altLabel'
}: CheckAltLabelProps): string | undefined => {
  let label
  item?.conditions?.some((condition) => {
    const { conditionMet, state, altLabel } = isConditionMet({
      condition,
      elementsMap,
      values,
      langVersion,
      connectedMap,
      errors
    })
    if (conditionMet && state === conditionType) {
      label = altLabel
      return true
    } else {
      return false
    }
  })
  return label
}

interface GetValidCurrentStepIndexProps {
  realIndex: number
  disabledIds: string[]
}

export const getValidCurrentStepIndex = ({
  realIndex,
  disabledIds
}: GetValidCurrentStepIndexProps): number => {
  let base = realIndex
  for (let index = realIndex; index >= 0; index--) {
    if (Array.isArray(disabledIds) && disabledIds.includes(`${sectionConditionId}${index}`)) {
      base--
    }
  }
  return base
}

export const sectionConditionId = 'section##'

interface GetDisabledIdsProps {
  sections: any[]
  errors: Record<string, any>
  [key: string]: any
}

export const getDisabledIds = ({
  sections,
  errors,
  ...props
}: GetDisabledIdsProps): string[] => {
  if (isFalsy(errors)) {
    return []
  }

  const getIdsToDisable = (errors: Record<string, any>): string[] => {
    const toRet: string[] = []
    sections.forEach((section, sectionIndex) => {
      const checkConditionsInItem = (item: FormElement): void => {
        if (isDisabled({ item, ...props, errors })) {
          if (isTruthy(item.elements)) {
            disableAllChildElements(item)
          } else {
            toRet.push(item.id)
          }
        } else if (isTruthy(item.elements)) {
          item.elements.forEach((item) => {
            checkConditionsInItem(item)
          })
        }
      }

      const disableAllChildElements = (item: FormElement): void => {
        toRet.push(item.id)
        if (isTruthy(item.elements)) {
          item.elements.forEach((item) => {
            disableAllChildElements(item)
          })
        }
      }

      if (isDisabled({ item: section, ...props, errors })) {
        toRet.push(`${sectionConditionId}${sectionIndex}`)
        section.elements.forEach((item) => {
          disableAllChildElements(item)
        })
      }

      section.elements.forEach((item) => {
        checkConditionsInItem(item)
      })
    })
    return toRet
  }

  const preDisabledIds = getIdsToDisable(errors)
  const validErrors = Object.fromEntries(
    Object.entries(errors).filter(([key]) => !preDisabledIds.includes(key))
  )
  return getIdsToDisable(validErrors)
}

interface IsConditionMetProps {
  condition: FormCondition
  pathname?: string
  elementsMap?: Record<string, FormElement>
  values?: Record<string, any>
  langVersion?: string
  describeMap?: Record<string, any>
  connectedMap?: ConnectedMap
  errors?: Record<string, any>
}

interface IsconditionMetReturn {
  conditionMet: boolean
  state: string
  altLabel: JSX.Element | string
}

export const isConditionMet = ({
  condition,
  pathname,
  elementsMap,
  values,
  langVersion,
  describeMap = {},
  connectedMap = {},
  errors = {}
}: IsConditionMetProps): IsconditionMetReturn => {
  let bool = false
  let testRule, testValue, ruleType
  if (isTruthy(condition.conditions)) {
    const logic = condition.logic ?? 'all'
    const customFormula = condition?.customFormula
    if (logic === 'any') {
      bool = condition.conditions.some((condition) => {
        const { conditionMet } = isConditionMet({
          condition,
          elementsMap,
          values,
          langVersion,
          describeMap,
          connectedMap,
          pathname
        })
        return conditionMet
      })
    } else if (logic === 'custom' && isTruthy(customFormula)) {
      const conditionResults = condition.conditions.map(condition => {
        const { conditionMet } = isConditionMet({
          condition,
          elementsMap,
          values,
          langVersion,
          describeMap,
          connectedMap,
          pathname
        })
        // return boolean value, since conditionMet can be an empty string
        if (conditionMet) {
          return true
        } else {
          return false
        }
        // return conditionMet
      })
      if (isCustomFormulaValid(customFormula, condition?.conditions?.length)) {
        const { result } = evaluateCustomFormula(customFormula, conditionResults)
        bool = result ?? false
      } else {
        bool = false
      }
    } else {
      bool = !condition.conditions.some((condition) => {
        const { conditionMet } = isConditionMet({
          condition,
          elementsMap,
          values,
          langVersion,
          describeMap,
          connectedMap,
          pathname
        })
        return !conditionMet
      })
    }
  } else {
    const toRet = {
      conditionMet: false,
      state: condition.state,
      altLabel: parseFormLabelText({
        text: condition?.altLabel,
        langVersion
      })
    }
    if (
      isTruthy(condition.conditionTarget) &&
      isTruthy(specialFormConditions[condition.conditionTarget])
    ) {
      if (condition.conditionTarget === 'noErrors') {
        toRet.conditionMet = Object.keys(errors).length === 0
      } else if (condition.conditionTarget === 'errorsPresent') {
        toRet.conditionMet = Object.keys(errors).length > 0
      } else if (
        condition.conditionTarget === 'inRoute' ||
        condition.conditionTarget === 'notInRoute'
      ) {
        const route = routeOptions?.find(
          (route) => route.id === condition.parameter
        )
        toRet.conditionMet =
          condition.conditionTarget === 'inRoute'
            ? matchPath(route?.path ?? '', pathname ?? '')
            : condition.conditionTarget === 'notInRoute'
              ? !matchPath(route?.path ?? '', pathname ?? '')
              : false
      }
      return toRet
    } else if (isFalsy(condition.condition)) {
      return toRet
    }
    if (
      !['isTrue', 'isFalse', 'isEmpty', 'isNotEmpty'].includes(
        condition.condition
      ) &&
      isFalsy(condition.parameter)
    ) {
      return toRet
    }

    const { childObject, collectionKey } = condition
    if (isTruthy(collectionKey) && childObject != null) {
      /** It means that condition is connected to a collection */
      /** Get object we connected to */
      const connectedObject = Object.values(connectedMap).find(
        (obj) => obj.objectType === condition.connectedObject
      )
      /** Get the collection */
      const collection =
        connectedObject?.additionalInfo?.[collectionKey]
      /** Get the values of the selected child object of the collection */
      const collectionValues = collection?.map(
        (item) => item[childObject]
      )
      ruleType = condition.type
      if (isTruthy(ruleType)) {
        testRule =
          formConditions?.[ruleType]?.conditions[condition.condition].rule
      }
      testValue = collectionValues
    } else if (isTruthy(condition.sfField) && condition.sfObject != null) {
      /** It means that condition is connected to SF object */
      const objData = connectedMap[condition.sfObject]
      if (isTruthy(objData)) {
        // Handle nested conditions target
        if (String(condition.sfField).includes('.')) {
          const objField = condition.sfField.split('.')[0]
          const nestedField = condition.sfField.split('.')[1]
          if (isFalsy(objData.sfObject[objField])) {
            return toRet
          }
          testValue = objData.sfObject[objField][nestedField]
          const nonRelationshipFieldName = objField.replace('__r', '__c')
          const nonRelationshipNestedFieldName = nestedField.replace(
            '__r',
            '__c'
          )

          const nestedObjectInfo =
            objData.fieldsMap[nonRelationshipFieldName] ??
            objData.fieldsMap[nonRelationshipNestedFieldName]
          const nestedObjectType = nestedObjectInfo?.referenceTo?.[0] ?? ''
          const nestedFieldInfo = describeMap[nestedObjectType]?.fields.find(
            (field) => field.name === nestedField
          )
          ruleType =
            sfFieldToConditionType[
              nestedFieldInfo?.type ?? nestedObjectInfo?.type
            ]
          if (isFalsy(ruleType)) {
            return toRet
          }
          testRule =
            formConditions[ruleType].conditions[condition.condition].rule
        } else {
          testValue = objData.sfObject[condition.sfField]
          ruleType =
            sfFieldToConditionType[objData.fieldsMap[condition.sfField]?.type]
          if (condition.sfField === 'RecordTypeId') {
            ruleType = 'picklist'
          }
        }
        if (isFalsy(ruleType)) {
          return toRet
        }
        testRule =
          formConditions[ruleType].conditions[condition.condition].rule
      }
    } else {
      /** It means that field is connected to form element */
      const targetElement = elementsMap?.[condition.conditionTarget]
      /** Check if the element exists */
      if (isFalsy(targetElement)) {
        console.warn(
          'Question targeted by this condition does not exist!',
          condition
        )
        return toRet
        /** Check if the element condition type is a standard one */
      } else if (
        targetElement?.elementType != null &&
        !Object.keys(formConditions).includes(targetElement?.elementType)
      ) {
        console.warn(
          'Question targeted by this condition has invalid type!',
          condition
        )
        return toRet
      }

      /** Determine the type of the element which is used to determine the test rule.
       * If the condition has a childObject, it means that the condition is related to a collection and specifically to a childObject of the collection.
       * So test rule is determined by the type of the childObject */
      const elementType = isTruthy(condition.childObject) &&
        targetElement?.elementType != null
        ? collectionElementTypes[targetElement.elementType].fields[
          condition.childObject
        ].type
        : targetElement?.elementType

      if (isFalsy(values) || values == null) {
        // This may happen if we check conditions before determining values
        // console.warn('No values object provided to resolve condition')
        return toRet
      }

      /** Get the value of the element to which the condition is connected.
       * If the condition has a childObject and a collectionCondition, it means that the value is an array
       * and the test value is an array of the values of the childObject
       */
      const { childObject, collectionCondition } = condition
      testValue =
        childObject != null && isTruthy(collectionCondition)
          ? values[condition.conditionTarget]?.map(
            (item) => item[childObject]
          )
          : values[condition.conditionTarget]

      testRule =
        formConditions[elementType].conditions[condition.condition]?.rule
    }

    if (isTruthy(testRule)) {
      // if the target is a budget element
      let targetElement

      if (isTruthy(elementsMap) && isTruthy(condition?.conditionTarget)) {
        targetElement = elementsMap[condition.conditionTarget]
      }

      if (targetElement?.elementType === 'budget') {
        let totalSum = 0
        const keysToCheck = ['expenses', 'revenues']

        keysToCheck.forEach((category) => {
          if (isTruthy(testValue?.[category])) {
            Object.keys(testValue[category]).forEach((year) => {
              const categoryData = testValue[category][year]
              if (
                condition?.budgetLine != null &&
                isTruthy(categoryData?.[condition?.budgetLine])
              ) {
                const value = parseFloat(categoryData[condition.budgetLine]?.value)
                totalSum += isNaN(value)
                  ? 0
                  : value
              }
            })
          }
        })

        testValue = totalSum.toString()
      }

      /** If the condition has a collectionKey, it means that value is an array
       * and the test rule is applied in accordance with the collectionCondition rule */
      if (isTruthy(condition.collectionKey)) {
        bool =
          collectionConditions[condition.collectionCondition]?.rule({
            testValue,
            testRule,
            conditionParameter: condition.parameter
          }) ?? false
        /** all other cases (default) */
      } else {
        bool = testRule(condition.parameter, testValue)
      }
    }
  }

  return {
    conditionMet: bool,
    state: condition.state,
    altLabel: parseFormLabelText({
      text: condition?.altLabel,
      langVersion
    })
  }
}
